import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";

const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL;
const SUPABASE_ANON_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY;

const API = `${SUPABASE_URL}/functions/v1/leads`;

const CONSOLES = [
  { id: "consultation", label: "Book Consultation", desc: "30-min strategy with a MUNNEXA partner.", color: "#00F0FF" },
  { id: "demo", label: "Talk to AI Demo", desc: "Live walkthrough with NEXA + automation showcase.", color: "#B200FF" },
  { id: "proposal", label: "Request Proposal", desc: "Custom AI roadmap shaped to your stack.", color: "#FF007F" },
  { id: "audit", label: "Free AI Audit", desc: "We map your business and ship 12+ growth ideas.", color: "#FF6000" },
];

export default function Contact() {
  const [interest, setInterest] = useState("consultation");
  const [form, setForm] = useState({ name: "", email: "", company: "", message: "" });
  const [sending, setSending] = useState(false);

const submit = async (e) => {
  e.preventDefault();

  if (!form.name.trim() || !form.email.trim()) {
    toast.error("Name and email required");
    return;
  }

  setSending(true);

  try {
    const response = await axios.post(
      API,
      {
        name: form.name.trim(),
        email: form.email.trim(),
        company: form.company.trim(),
        message: form.message.trim(),
        interest,
      },
      {
        timeout: 30000,
        headers: {
          "Content-Type": "application/json",
          apikey: SUPABASE_ANON_KEY,
          Authorization: `Bearer ${SUPABASE_ANON_KEY}`,
        },
      }
    );

    console.log("Lead submitted:", response.data);

    toast.success(
      "Signal received — a MUNNEXA operator will reach out shortly."
    );

    setForm({
      name: "",
      email: "",
      company: "",
      message: "",
    });
  } catch (error) {
    console.error("LEAD SUBMISSION ERROR:", error);

    if (error.response) {
      console.error("Status:", error.response.status);
      console.error("Response:", error.response.data);

      const serverError =
        error.response.data?.error ||
        error.response.data?.message ||
        error.response.data?.details;

      toast.error(
        serverError || `Submission failed (${error.response.status})`
      );
    } else if (error.request) {
      toast.error(
        "Could not reach MUNNEXA. Please check the connection."
      );
    } else {
      toast.error("Unable to submit. Please retry.");
    }
  } finally {
    setSending(false);
  }
};

  return (
    <section id="contact" className="relative py-28 px-6" data-testid="contact-section">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-12">
          <p className="font-mono text-[11px] tracking-[0.4em] text-cyan-300/80 mb-3">◆ CONTROL ROOM</p>
          <h2 className="text-4xl sm:text-5xl font-[Outfit] font-bold">Open a channel to <span className="text-gradient">MUNNEXA.</span></h2>
          <p className="mt-3 text-white/65 max-w-2xl mx-auto">Pick a console. Light it up. We respond within 24 hours.</p>
        </div>

        <div className="grid lg:grid-cols-5 gap-6">
          <div className="lg:col-span-2 space-y-3">
            {CONSOLES.map((c) => (
              <button
                key={c.id}
                onClick={() => setInterest(c.id)}
                data-testid={`console-${c.id}`}
                className={`w-full text-left glass rounded-2xl p-4 border transition group ${
                  interest === c.id ? "border-white/30" : "border-white/10 hover:border-white/20"
                }`}
                style={interest === c.id ? { boxShadow: `0 0 32px ${c.color}55, inset 0 0 24px ${c.color}22` } : {}}
              >
                <div className="flex items-center gap-3">
                  <span className="w-3 h-3 rounded-full animate-pulse" style={{ background: c.color, boxShadow: `0 0 12px ${c.color}` }} />
                  <div className="font-[Outfit] font-semibold">{c.label}</div>
                </div>
                <div className="mt-1 text-xs text-white/55 pl-6">{c.desc}</div>
              </button>
            ))}
          </div>

          <form onSubmit={submit} className="lg:col-span-3 glass-strong holo-border rounded-3xl p-6 sm:p-8 space-y-4" data-testid="contact-form">
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="font-mono text-[10px] uppercase tracking-widest text-white/55">Name</label>
                <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="input-name" className="mt-1 w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-sm outline-none focus:border-cyan-400/60" />
              </div>
              <div>
                <label className="font-mono text-[10px] uppercase tracking-widest text-white/55">Email</label>
                <input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} type="email" data-testid="input-email" className="mt-1 w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-sm outline-none focus:border-cyan-400/60" />
              </div>
            </div>
            <div>
              <label className="font-mono text-[10px] uppercase tracking-widest text-white/55">Company</label>
              <input value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} data-testid="input-company" className="mt-1 w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-sm outline-none focus:border-cyan-400/60" />
            </div>
            <div>
              <label className="font-mono text-[10px] uppercase tracking-widest text-white/55">Tell us about your next era</label>
              <textarea value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} rows={4} data-testid="input-message" className="mt-1 w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-sm outline-none focus:border-cyan-400/60 resize-none" />
            </div>
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div className="font-mono text-[10px] uppercase tracking-widest text-white/55">Selected: <span className="text-cyan-300">{interest}</span></div>
              <button type="submit" disabled={sending} data-testid="contact-submit" className="btn-gradient rounded-full px-8 py-3 text-sm">
                {sending ? "Transmitting..." : "Initiate Contact →"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
}
